import { IntlShape } from 'react-intl';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import { SongUploadStatusKind } from '../songUpload';
import { soundCloudStatusMessages as m } from '../soundCloudMessages';

export type StatusChipTone = 'ok' | 'work' | 'bad' | 'muted';

export interface SongUploadStatusTexts {
  head: string;
  sub: string;
  chip: string;
  tone: StatusChipTone;
  /** The plain line or the red alert of the state. */
  body: string;
  isAlert: boolean;
}

function day(intl: IntlShape, iso: string | null): string {
  return iso === null
    ? ''
    : intl.formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function dayTime(intl: IntlShape, iso: string | null): string {
  return iso === null
    ? ''
    : intl.formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The texts of each state of the status panel (design c6). */
export function songUploadStatusTexts(
  intl: IntlShape,
  kind: SongUploadStatusKind,
  record: SerializedSongPublish
): SongUploadStatusTexts {
  const failedAt = dayTime(intl, record.updatedAt ?? record.createdAt);
  const failed = (body: string, chip = m.chipFailed): SongUploadStatusTexts => ({
    head: intl.formatMessage(m.failedHead),
    sub: failedAt,
    chip: intl.formatMessage(chip),
    tone: 'bad',
    body,
    isAlert: true,
  });
  switch (kind) {
    case 'waiting':
      return {
        head: intl.formatMessage(m.waitingHead, { date: day(intl, record.publishAt) }),
        sub: intl.formatMessage(m.waitingSub, {
          sharing: intl.formatMessage(record.sharing === 'private' ? m.sharingPrivate : m.sharingPublic),
          time:
            record.publishAt === null ? '' : intl.formatTime(record.publishAt, { hour: '2-digit', minute: '2-digit' }),
        }),
        chip: intl.formatMessage(m.chipWaiting),
        tone: 'work',
        body: intl.formatMessage(m.waitingBody),
        isAlert: false,
      };
    case 'uploading':
      return {
        head: intl.formatMessage(m.uploadingHead),
        sub: '',
        chip: intl.formatMessage(m.chipUploading),
        tone: 'work',
        body: intl.formatMessage(m.uploadingBody),
        isAlert: false,
      };
    case 'live':
      return {
        head: intl.formatMessage(m.liveHead),
        sub: intl.formatMessage(m.liveSub, { date: dayTime(intl, record.publishedAt) }),
        chip: intl.formatMessage(m.chipLive),
        tone: 'ok',
        body: intl.formatMessage(m.liveBody),
        isAlert: false,
      };
    case 'private':
      return {
        head: intl.formatMessage(m.privateHead),
        sub: intl.formatMessage(m.privateSub, { date: dayTime(intl, record.publishedAt) }),
        chip: intl.formatMessage(m.chipPrivate),
        tone: 'muted',
        body: intl.formatMessage(m.privateBody),
        isAlert: false,
      };
    case 'removed':
      return {
        head: intl.formatMessage(m.removedHead),
        sub: intl.formatMessage(m.removedSub, { date: day(intl, record.updatedAt) }),
        chip: intl.formatMessage(m.chipRemoved),
        tone: 'bad',
        body: intl.formatMessage(m.removedBody),
        isAlert: true,
      };
    case 'refused':
      return failed(intl.formatMessage(m.refusedBody, { detail: record.failDetail ?? '' }).trim());
    case 'unsure':
      return failed(intl.formatMessage(m.unsureBody));
    case 'master':
      return failed(intl.formatMessage(m.masterBody));
    case 'reconnect':
      return failed(intl.formatMessage(m.reconnectBody), m.chipSignedOut);
    case 'failed':
      return failed(intl.formatMessage(m.failedBody));
  }
}
